import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Button } from 'primereact/button';
import { useHistory, useLocation } from 'react-router-dom';

import APIService from '../services/API';
import { useInstantReserve, useNewPriceQuotes, useUser, useV2Instants } from '../store/hooks/hooks';
import {
  docDaBoQua,
  docDaBoQuaTab,
  ghiDaBoQua,
  ghiDaBoQuaTab,
  KHOA_BO_QUA_TOI_LUOT,
  LuotCuaToi,
  LuotDangToi,
  tinhThongBaoToiLuot,
} from '../pages/instants/luotToiLuot';

import './LuotVanHanhThongBao.scss';

/**
 * Thông báo "TỚI LƯỢT CHỜ VẬN HÀNH" ở mọi trang (chính giữa màn hình). Dữ liệu từ GET /instants/luot-cua-toi
 * (chỉ các lượt của chính tôi), tải khi mở, mỗi 15 giây và khi có sự kiện lịch ưu tiên; xét tới lượt mỗi giây.
 * Ba nút:
 *  - MỞ ĐƠN HÀNG NHANH → /don-hang-nhanh?mo_popup_uu_tien=1, trang mở ngay popup InstantMatching (mọi nút là của trang);
 *  - ĐÓNG THÔNG BÁO TAB NÀY → bỏ qua các lượt đang hiện ở RIÊNG tab trình duyệt này (sessionStorage), tab khác vẫn hiện;
 *  - ĐÓNG Ở TẤT CẢ TAB → bỏ qua ở MỌI tab (localStorage; tab khác nhận qua sự kiện `storage` và ẩn ngay).
 * Bỏ qua chỉ có hiệu lực cho tới khi có lượt mới (khóa owner_id|start_time). Không hiện khi đang ở trang Đơn hàng nhanh.
 */
const GIAY_TAI_LAI = 15;
export const THAM_SO_MO_POPUP_UU_TIEN = 'mo_popup_uu_tien';

export default function LuotVanHanhThongBao() {
  const user = useUser();
  const history = useHistory();
  const location = useLocation();
  const instantReserve = useInstantReserve();
  const instants = useV2Instants();
  const newPriceQuotes = useNewPriceQuotes();

  const [luots, setLuots] = useState<LuotCuaToi[]>([]);
  const [dangToiLuot, setDangToiLuot] = useState<LuotDangToi[]>([]);
  const [hienThi, setHienThi] = useState<boolean>(false);
  // Khóa đã bỏ qua: tab này (sessionStorage) ∪ mọi tab (localStorage)
  const daBoQuaRef = useRef<string[]>([...docDaBoQuaTab(), ...docDaBoQua()]);
  const luotsRef = useRef<LuotCuaToi[]>([]);

  const taiLuot = useCallback(async () => {
    if (!user?.id || document.hidden) {
      return;
    }
    try {
      const res = await APIService.fetchLuotCuaToi();
      if (res?.success && Array.isArray(res.data)) {
        setLuots(res.data);
      }
    } catch (e) {
      // thông báo chỉ để nhắc, lỗi mạng không chặn trang
    }
  }, [user?.id]);

  const tick = useCallback(() => {
    const kq = tinhThongBaoToiLuot(luotsRef.current, moment(), daBoQuaRef.current);
    setDangToiLuot(kq.dangToiLuot);
    setHienThi(kq.coKhoaMoi);
  }, []);

  useEffect(() => {
    taiLuot();
    const timer = setInterval(taiLuot, GIAY_TAI_LAI * 1000);
    return () => clearInterval(timer);
  }, [taiLuot]);

  // Lịch TG ưu tiên vừa đổi (người khác rút lượt, giảm phút, chốt đơn…) → tải lại ngay.
  useEffect(() => {
    taiLuot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instantReserve, instants, newPriceQuotes]);

  useEffect(() => {
    luotsRef.current = luots;
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [luots, tick]);

  // Tab khác bấm "ĐÓNG Ở TẤT CẢ TAB" → localStorage đổi → sự kiện `storage` → ẩn ngay ở tab này.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KHOA_BO_QUA_TOI_LUOT || e.key === null) {
        daBoQuaRef.current = Array.from(new Set([...docDaBoQuaTab(), ...docDaBoQua()]));
        tick();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [tick]);

  if (!user?.id || !hienThi || dangToiLuot.length === 0 || location.pathname.startsWith('/don-hang-nhanh')) {
    return null;
  }

  const khoasDangHien = () => dangToiLuot.map((it) => it.khoa);

  const dongTabNay = () => {
    const khoas = khoasDangHien();
    daBoQuaRef.current = Array.from(new Set([...daBoQuaRef.current, ...khoas]));
    ghiDaBoQuaTab(khoas);
    setHienThi(false);
  };

  const dongTatCaTab = () => {
    const khoas = khoasDangHien();
    daBoQuaRef.current = Array.from(new Set([...daBoQuaRef.current, ...khoas]));
    ghiDaBoQua(khoas);
    setHienThi(false);
  };

  const moDonHangNhanh = () => {
    history.push(`/don-hang-nhanh?${THAM_SO_MO_POPUP_UU_TIEN}=1`);
  };

  return (
    <div className="luot-van-hanh-thong-bao">
      <div className="luot-van-hanh-thong-bao-tieu-de">
        <i className="pi pi-bell" /> ĐẾN LƯỢT CHỜ VẬN HÀNH ({dangToiLuot.length} ĐƠN)
        <button
          type="button"
          className="luot-van-hanh-thong-bao-dong"
          onClick={dongTabNay}
          title="Đóng thông báo ở tab này"
        >
          ×
        </button>
      </div>
      {dangToiLuot.map((it) => (
        <div key={it.khoa} className="luot-van-hanh-thong-bao-dong-don">
          <strong>{it.owner_id}</strong> ({it.phut} PHÚT){it.deliver_route ? ` · ${it.deliver_route}` : ''} · xe{' '}
          {it.order_id}
        </div>
      ))}
      <div className="luot-van-hanh-thong-bao-nut">
        <Button
          label="MỞ ĐƠN HÀNG NHANH"
          icon="pi pi-external-link"
          className="p-button-danger"
          onClick={moDonHangNhanh}
        />
        <Button label="ĐÓNG THÔNG BÁO TAB NÀY" icon="pi pi-times" className="p-button-secondary" onClick={dongTabNay} />
        <Button
          label="ĐÓNG Ở TẤT CẢ TAB"
          icon="pi pi-times-circle"
          className="p-button-secondary"
          onClick={dongTatCaTab}
        />
      </div>
    </div>
  );
}
